import { CommunicationCategory, TimelineFilterType } from '@bariendo/lib/constants';
import { ActionIcon, Box, Flex, Group, Text, TextInput, Tooltip } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { createReference } from '@medplum/core';
import { Practitioner, Reference } from '@medplum/fhirtypes';
import { ResourceInput, useResource } from '@medplum/react';
import {
  IconCalendar,
  IconCreditCard,
  IconFileText,
  IconFilter,
  IconMenu,
  IconMessage,
  IconSearch,
  IconTestPipe,
  IconX,
} from '@tabler/icons-react';
import clsx from 'clsx';
import { ReactElement, useMemo, useState } from 'react';
import classes from './TimelineFilters.module.css';
import dayjs from 'dayjs';
import { ACTIVITY_CATEGORY_COLORS, ActivityCategory } from '../activityCategories';

export type FilterType =
  | typeof TimelineFilterType.ALL
  | typeof TimelineFilterType.COMMUNICATIONS
  | typeof TimelineFilterType.DOCUMENTATION
  | typeof TimelineFilterType.CARE_CLINICAL
  | typeof TimelineFilterType.APPOINTMENT
  | typeof TimelineFilterType.BILLING
  | typeof CommunicationCategory.SMS_MESSAGE
  | typeof CommunicationCategory.PHONE_CALL
  | typeof CommunicationCategory.CHAT
  | typeof CommunicationCategory.STANDALONE_NOTE
  | typeof CommunicationCategory.LAB_ORDER
  | typeof CommunicationCategory.ENCOUNTER
  | typeof CommunicationCategory.INBOUND
  | typeof CommunicationCategory.FAX_MESSAGE;

export type FilterContext = 'Patient' | 'Partner';

export interface FilterOption {
  label: string;
  icon: React.ReactNode;
  value: FilterType;
  context: FilterContext[];
  // null = "All" (neutral, no category color); otherwise the category whose
  // color drives both the icon and the active-state tint per canon (see
  // ../activityCategories.ts).
  category: ActivityCategory | null;
}

// Canon icons follow activity-timeline.mdx: chat bubble for Conversations,
// file-with-lines for Notes, lab tube for Orders & results, calendar for
// Appointments, credit card for Billing (the billing marker's glyph). "All"
// stays as the menu icon (neutral chrome).
export const FILTERS: FilterOption[] = [
  {
    label: 'All',
    icon: <IconMenu size={14} />,
    value: TimelineFilterType.ALL,
    context: ['Patient', 'Partner'],
    category: null,
  },
  {
    label: 'Conversations',
    icon: <IconMessage size={14} />,
    value: TimelineFilterType.COMMUNICATIONS,
    context: ['Patient', 'Partner'],
    category: 'conversations',
  },
  {
    label: 'Notes',
    icon: <IconFileText size={14} />,
    value: TimelineFilterType.DOCUMENTATION,
    context: ['Patient', 'Partner'],
    category: 'notes',
  },
  {
    label: 'Orders & results',
    icon: <IconTestPipe size={14} />,
    value: TimelineFilterType.CARE_CLINICAL,
    context: ['Patient'],
    category: 'ordersResults',
  },
  {
    label: 'Appointments',
    icon: <IconCalendar size={14} />,
    value: TimelineFilterType.APPOINTMENT,
    context: ['Patient'],
    category: 'appointments',
  },
  {
    label: 'Billing',
    icon: <IconCreditCard size={14} />,
    value: TimelineFilterType.BILLING,
    context: ['Patient'],
    category: 'billing',
  },
];

/**
 * Which filter buttons are visible for a given timeline context and viewer.
 *
 * Three gates, each dropping the entry from the array rather than disabling it:
 * - context: a filter shows when the current context is in its allow-list
 *   (Appointments, Orders & results and Billing are Patient-only; All /
 *   Conversations / Notes appear in both Patient and Partner contexts);
 * - `clinical`: false drops Orders & results, the chart (BAR-2585);
 * - `billing`: false drops Billing (BAR-2646). Defaults to false.
 *
 * There is intentionally NO per-filter feature-flag lookup in here. The
 * scheduling / labs / timeline-charting flags were removed once those features
 * shipped (BAR-1879). During that cleanup the Appointments guard was left as a
 * bare `filter.value === APPOINTMENT → return false`, which silently hid the
 * Appointments filter in every context (BAR-2440). Callers decide the booleans;
 * don't add bare per-filter exclusions here.
 */
export function getEnabledFilters(
  filters: FilterOption[],
  context?: FilterContext,
  clinical = true,
  billing = false
): FilterOption[] {
  return filters.filter(
    (filter) =>
      (!context || filter.context.includes(context)) &&
      // Orders & results is the chart; Patient Access never sees it (BAR-2585).
      (clinical || filter.value !== TimelineFilterType.CARE_CLINICAL) &&
      // Billing needs canViewPayments (BAR-2646). Fails closed: a caller that does not
      // say gets no Billing chip.
      (billing || filter.value !== TimelineFilterType.BILLING)
  );
}

interface TimelineFiltersProps {
  activeFilter: FilterType;
  onFilterChange: (filter: FilterType) => void;
  searchTerm: string;
  onSearchChange: (searchTerm: string) => void;
  selectedProvider?: Reference<Practitioner> | null;
  onProviderChange: (provider: Reference<Practitioner> | null) => void;
  startDate?: Date | null;
  endDate?: Date | null;
  onStartDateChange: (date: Date | null) => void;
  onEndDateChange: (date: Date | null) => void;
  context?: FilterContext; // Optional: filter which filters to show based on context
  /** False hides Orders & results — the chart — for roles that never see it (BAR-2585). */
  clinical?: boolean;
  /** True shows the Billing filter: pass `canViewPayments` (BAR-2646). Hidden by default. */
  billing?: boolean;
}

export function TimelineFilters({
  activeFilter,
  onFilterChange,
  searchTerm,
  onSearchChange,
  selectedProvider,
  onProviderChange,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  context,
  clinical = true,
  billing = false,
}: TimelineFiltersProps): ReactElement {
  const [showSearch, setShowSearch] = useState(false);
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  const providerResource = useResource(selectedProvider || undefined) as Practitioner | undefined;

  const hasActiveSearch = useMemo(() => Boolean(searchTerm.trim()), [searchTerm]);
  const hasActiveDateFilters = useMemo(() => Boolean(startDate || endDate), [startDate, endDate]);
  const hasActiveProviderFilter = useMemo(() => Boolean(selectedProvider), [selectedProvider]);
  const hasActiveFilters = useMemo(
    () => hasActiveDateFilters || hasActiveProviderFilter,
    [hasActiveDateFilters, hasActiveProviderFilter]
  );

  const enabledFilterButtons = useMemo(
    () => getEnabledFilters(FILTERS, context, clinical, billing),
    [context, clinical, billing]
  );

  const handleSearchToggle = () => {
    const newShowSearch = !showSearch;
    setShowSearch(newShowSearch);
    if (!newShowSearch) {
      onSearchChange('');
    }
  };

  const handleDateChange = (date: Date | null, edge: 'start' | 'end' = 'start') => {
    if (!date) {
      return edge === 'start' ? onStartDateChange(null) : onEndDateChange(null);
    }
    // Ensure the date is in local timezone
    const localDate = new Date(date);
    localDate.setHours(
      edge === 'start' ? 0 : 23,
      edge === 'start' ? 0 : 59,
      edge === 'start' ? 0 : 59,
      edge === 'start' ? 0 : 999
    );
    if (edge === 'start') {
      onStartDateChange(localDate);
    } else {
      onEndDateChange(localDate);
    }
  };

  const handleFilterIconClick = () => {
    setFiltersExpanded(!filtersExpanded || hasActiveFilters);
  };

  const getActionIconStyle = (isActive: boolean) => ({
    backgroundColor: isActive ? 'rgba(36, 106, 122, 0.1)' : undefined,
    color: isActive ? '#246A7A' : '#223843',
  });

  return (
    <Box p="md">
      {/* Left: Filter buttons */}
      <Flex mih={40} justify="space-between" style={{ flex: 1 }}>
        <Group gap="xs">
          {enabledFilterButtons.map((filter) => {
            const isActive = activeFilter === filter.value;
            const categoryColor = filter.category ? ACTIVITY_CATEGORY_COLORS[filter.category] : null;
            // Color identity rules — the active chip mirrors the activity
            // marker dot in the timeline rail so the eye connects "Sage chip
            // selected" with "Sage dots below":
            // - Active = full-saturation category fill + Warm White text +
            //   Warm White icon. Same look as the 24px marker dot, just on
            //   a chip silhouette.
            // - Inactive = transparent bg, neutral 0.55 text, full-saturation
            //   icon (color is identity, not state). Hover wash uses the
            //   same category color at 8%.
            // - "All" (no category) keeps the legacy neutral chrome.
            const inlineStyle: React.CSSProperties = categoryColor
              ? {
                  color: isActive ? '#FAFCF7' : 'rgba(0, 0, 0, 0.55)',
                  backgroundColor: isActive ? categoryColor : 'transparent',
                  border: '1px solid transparent',
                }
              : {};
            const iconColor = categoryColor ? (isActive ? '#FAFCF7' : categoryColor) : undefined;
            return (
              <Tooltip key={filter.value} openDelay={400} label="Show only this type">
                <Box
                  onClick={() => onFilterChange(filter.value)}
                  className={clsx(
                    classes.timelineMenuButton,
                    // "All" filter still uses the legacy active/inactive chrome.
                    filter.category === null && (isActive ? classes.menuActive : classes.menuInactive),
                    filter.category === null && !isActive && classes.actionItem,
                    // Categorical filters use inline styles + a single class
                    // for hover behavior keyed off the data-category attr.
                    filter.category !== null && classes.categoricalFilter
                  )}
                  style={inlineStyle}
                  data-category={filter.category ?? undefined}
                  data-active={isActive || undefined}
                >
                  <span style={{ color: iconColor, display: 'inline-flex' }}>{filter.icon}</span>
                  <Text variant="label" style={{ color: 'inherit' }}>
                    {filter.label}
                  </Text>
                </Box>
              </Tooltip>
            );
          })}
        </Group>

        <Group gap="xs">
          {showSearch || hasActiveSearch ? (
            <TextInput
              placeholder="Search timeline content..."
              value={searchTerm}
              onChange={(e) => onSearchChange(e.currentTarget.value)}
              rightSection={
                <ActionIcon variant="subtle" color="gray" size="sm" onClick={handleSearchToggle}>
                  <IconX size={16} />
                </ActionIcon>
              }
              w={200}
              autoFocus
            />
          ) : (
            <Tooltip openDelay={400} label="Search timeline content">
              <ActionIcon
                variant="subtle"
                color="#223843"
                size="md"
                onClick={handleSearchToggle}
                style={getActionIconStyle(hasActiveSearch)}
              >
                <IconSearch size={18} />
              </ActionIcon>
            </Tooltip>
          )}

          <Tooltip openDelay={400} label="Toggle filters">
            <ActionIcon
              variant="subtle"
              color="#223843"
              size="md"
              onClick={handleFilterIconClick}
              style={getActionIconStyle(hasActiveFilters)}
            >
              <IconFilter size={18} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Flex>

      {filtersExpanded && (
        <Group justify="flex-end" gap="xs" pt="md">
          <Box w={250}>
            <ResourceInput
              key={providerResource?.id || 'no-provider'}
              name="provider"
              resourceType="Practitioner"
              onChange={(resource) => {
                if (resource) {
                  onProviderChange(createReference(resource as Practitioner));
                } else {
                  onProviderChange(null);
                }
              }}
              placeholder="Filter by provider"
              defaultValue={providerResource || undefined}
            />
          </Box>
          <Box w={150}>
            <DatePickerInput
              placeholder="From date"
              value={startDate}
              onChange={(date) => handleDateChange(dayjs(date).toDate(), 'start')}
              clearable
              size="sm"
            />
          </Box>
          <Box w={150}>
            <DatePickerInput
              placeholder="To date"
              value={endDate}
              onChange={(date) => handleDateChange(dayjs(date).toDate(), 'end')}
              clearable
              size="sm"
            />
          </Box>
        </Group>
      )}
    </Box>
  );
}
